/**
 * Xero connection data access (issue #91).
 *
 * Mirrors go-service/internal/api/xero_admin.go: the OAuth callback is
 * SPA-relayed — Xero redirects the browser to /settings/xero-callback,
 * which posts {code, state} back through the authenticated admin API.
 * These endpoints return their payload as the whole body — no envelope.
 */
import { resource } from "./resource";

export type XeroStatus =
  "pending" | "pending_tenant" | "active" | "broken" | "disconnected";

export interface XeroConnectionStatus {
  status: XeroStatus;
  tenantName?: string;
  revenueAccountCode?: string;
  paymentAccountCode?: string;
  lastError?: string;
  /** Sync jobs that failed terminally — never reached Xero. */
  failedJobs?: number;
  /** Sync jobs held until the connection is reconnected. */
  parkedJobs?: number;
}

export interface XeroTenant {
  tenantId: string;
  tenantName: string;
}

export interface XeroCallbackResult {
  status: "active" | "pending_tenant";
  tenantName?: string;
  tenants?: XeroTenant[];
}

export const getXeroStatus = (
  signal?: AbortSignal,
): Promise<XeroConnectionStatus> =>
  resource<XeroConnectionStatus>("/api/admin/xero/status").get(signal);

export const startXeroConnect = (): Promise<{ authorizeUrl: string }> =>
  resource<{ authorizeUrl: string }>("/api/admin/xero/connect").post({});

export const completeXeroCallback = (
  code: string,
  state: string,
): Promise<XeroCallbackResult> =>
  resource<XeroCallbackResult>("/api/admin/xero/callback").post({
    code,
    state,
  });

export const pickXeroTenant = (
  tenantId: string,
): Promise<{ status: string; tenantName: string }> =>
  resource<{ status: string; tenantName: string }>(
    "/api/admin/xero/tenant",
  ).post({ tenantId });

export const saveXeroSettings = async (payload: {
  revenueAccountCode: string;
  paymentAccountCode: string;
}): Promise<void> => {
  await resource<void>("/api/admin/xero/settings").put(payload);
};

export const disconnectXero = async (): Promise<void> => {
  await resource<void>("/api/admin/xero/disconnect").post({});
};
